import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/navbar'
import { getDisplayPlatformName } from '../types/donation'
import { giftAidApi, GiftAidDonor, GiftAidDonorTransaction, MultiDeclarationRequest, FormsZipResult, MergeCheck, MergeReason, MERGE_REASONS } from '../services/gift-aid-api'
import {
  ClaimPeriod,
  PeriodDonor,
  SheetId,
  claimPeriodOptions,
  customPeriod,
  periodFileTag,
  lookbackStart,
  donorsInPeriod,
  claimableDonors,
  buildHmrcRows,
  countHmrcProblems,
  summarizeHmrcIssues,
  buildWorkbook,
  buildHmrcCsv,
  saveBlob,
  HMRC_MAX_ROWS,
} from '../services/gift-aid-export'

type Tab = 'all' | 'consented' | 'not_consented' | 'duplicates'

const formatCurrency = (amountInPence: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInPence / 100)
}

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const giftAidValue = (amountInPence: number): number => Math.round(amountInPence * 0.25)

const donorName = (d: GiftAidDonor): string =>
  [d.title, d.firstName, d.lastName].filter(Boolean).join(' ')

const donorAddress = (d: GiftAidDonor): string =>
  [d.addressLine1, d.city, d.postcode].filter(Boolean).join(', ')

const hasFullAddress = (d: GiftAidDonor): boolean =>
  Boolean(d.firstName && d.lastName && d.addressLine1 && d.city && d.postcode)

// A declaration request from the donor's saved details, covering all their donations
const requestFromDonor = (d: GiftAidDonor): MultiDeclarationRequest => ({
  title: d.title,
  firstName: d.firstName,
  lastName: d.lastName,
  addressLine1: d.addressLine1,
  addressLine2: d.addressLine2,
  city: d.city,
  postcode: d.postcode,
  email: d.email,
  transactionIds: d.transactions.map(t => t.id),
})

// Pulls the API's error message out of an Amplify error, if there is one
const apiErrorMessage = (err: any, fallback: string): string => {
  const message = err?.response?.data?.error
  return typeof message === 'string' && message ? message : fallback
}

// Opens a tab straight away (so popup blockers allow it), then points it at the PDF
const openPdf = async (getUrl: () => Promise<string>) => {
  const win = window.open('', '_blank')
  try {
    const url = await getUrl()
    if (win) {
      win.location.href = url
    } else {
      window.location.href = url
    }
  } catch (err) {
    win?.close()
    throw err
  }
}

const inputClass = 'w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500'

interface DeclarationFormProps {
  donor: GiftAidDonor
  requireConfirmation: boolean
  isSubmitting: boolean
  onSubmit: (req: MultiDeclarationRequest) => void
  onCancel: () => void
}

const DeclarationForm: React.FC<DeclarationFormProps> = ({ donor, requireConfirmation, isSubmitting, onSubmit, onCancel }) => {
  const [title, setTitle] = useState(donor.title)
  const [firstName, setFirstName] = useState(donor.firstName)
  const [lastName, setLastName] = useState(donor.lastName)
  const [addressLine1, setAddressLine1] = useState(donor.addressLine1)
  const [addressLine2, setAddressLine2] = useState(donor.addressLine2)
  const [city, setCity] = useState(donor.city)
  const [postcode, setPostcode] = useState(donor.postcode)
  const [email, setEmail] = useState(donor.email)
  const [confirmed, setConfirmed] = useState(false)

  const fieldsValid = Boolean(firstName.trim() && lastName.trim() && addressLine1.trim() && city.trim() && postcode.trim())
  const isValid = fieldsValid && (!requireConfirmation || confirmed)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    onSubmit({
      title,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      addressLine1: addressLine1.trim(),
      addressLine2: addressLine2.trim(),
      city: city.trim(),
      postcode: postcode.trim(),
      email: email.trim(),
      transactionIds: donor.transactions.map(t => t.id),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 pt-3 border-t border-gray-200">
      <h4 className="text-sm font-medium text-gray-700 mb-2">Claim Gift Aid</h4>
      <p className="text-xs text-gray-500 mb-3">
        Enter the donor's details to claim Gift Aid on all their donations (25% extra from HMRC).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <label htmlFor={`title-${donor.key}`} className="block text-xs text-gray-500 mb-1">Title</label>
          <select id={`title-${donor.key}`} value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass}>
            <option value="">-</option>
            <option value="Mr">Mr</option>
            <option value="Mrs">Mrs</option>
            <option value="Ms">Ms</option>
            <option value="Miss">Miss</option>
            <option value="Dr">Dr</option>
          </select>
        </div>
        <div>
          <label htmlFor={`first-${donor.key}`} className="block text-xs text-gray-500 mb-1">First Name *</label>
          <input id={`first-${donor.key}`} type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label htmlFor={`last-${donor.key}`} className="block text-xs text-gray-500 mb-1">Last Name *</label>
          <input id={`last-${donor.key}`} type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputClass} />
        </div>
      </div>
      <div className="mb-3">
        <label htmlFor={`address1-${donor.key}`} className="block text-xs text-gray-500 mb-1">Address Line 1 *</label>
        <input id={`address1-${donor.key}`} type="text" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} className={inputClass} />
      </div>
      <div className="mb-3">
        <label htmlFor={`address2-${donor.key}`} className="block text-xs text-gray-500 mb-1">Address Line 2</label>
        <input id={`address2-${donor.key}`} type="text" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} className={inputClass} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label htmlFor={`city-${donor.key}`} className="block text-xs text-gray-500 mb-1">City *</label>
          <input id={`city-${donor.key}`} type="text" value={city} onChange={(e) => setCity(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label htmlFor={`postcode-${donor.key}`} className="block text-xs text-gray-500 mb-1">Postcode *</label>
          <input id={`postcode-${donor.key}`} type="text" value={postcode} onChange={(e) => setPostcode(e.target.value)} className={inputClass} />
        </div>
      </div>
      <div className="mb-4">
        <label htmlFor={`email-${donor.key}`} className="block text-xs text-gray-500 mb-1">Email (optional)</label>
        <input id={`email-${donor.key}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
      </div>

      {requireConfirmation && (
        <label className="flex items-start gap-2 bg-purple-50 rounded-lg p-3 mb-3 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span>
            The donor has confirmed they are a UK taxpayer and want to Gift Aid these donations,
            any donations from the past 4 years and any future donations to Ross Street Masjid.
          </span>
        </label>
      )}

      <p className="text-xs text-gray-500 mb-3">
        This covers <span className="font-medium text-gray-700">all {donor.transactionCount} donations ({formatCurrency(donor.totalAmount)})</span> and
        generates one multiple-donation form.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Generating...' : 'Submit & generate form'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800"
        >
          Cancel
        </button>
        {!isValid && (
          <span className="text-xs text-gray-500">
            {!fieldsValid ? 'Fill in the required fields.' : 'Tick the donor confirmation to continue.'}
          </span>
        )}
      </div>
    </form>
  )
}

interface DonorCardProps {
  periodDonor: PeriodDonor
  isGenerating: boolean
  isDownloading: boolean
  onGenerate: (donor: GiftAidDonor, req?: MultiDeclarationRequest) => void
  onDownload: (donor: GiftAidDonor) => void
  selected: boolean
  onToggleSelect: (donor: GiftAidDonor) => void
  onUnmerge: (donor: GiftAidDonor) => void
}

const DonorCard: React.FC<DonorCardProps> = ({ periodDonor, isGenerating, isDownloading, onGenerate, onDownload, selected, onToggleSelect, onUnmerge }) => {
  const { donor, transactions, total, coveredTotal } = periodDonor
  const [isExpanded, setIsExpanded] = useState(false)
  const [showForm, setShowForm] = useState(false)

  const handleGenerateClick = () => {
    // Consented donors with full details skip the form
    if (donor.consented && hasFullAddress(donor)) {
      onGenerate(donor)
      return
    }
    setShowForm(true)
    setIsExpanded(true)
  }

  const address = donorAddress(donor)

  return (
    <div className={`rounded-lg border ${selected ? 'border-teal-500 ring-1 ring-teal-500 bg-teal-50/40' : 'bg-white border-gray-200'}`}>
      <div className="p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 min-w-0 flex gap-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(donor)}
            aria-label={`Select ${donorName(donor)} to merge`}
            className="mt-1 h-4 w-4 shrink-0 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
          />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-gray-900">{donorName(donor)}</span>
              {donor.consented ? (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700">Consented</span>
              ) : (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 text-amber-700">No consent</span>
              )}
              {donor.merge && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-50 text-purple-700">Merged</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5 break-words">
              {donor.email || 'No email'} · {address || 'No address yet'}
            </p>
            {donor.merge && (
              <div className="mt-2 pt-2 border-t border-dashed border-gray-200 text-xs text-gray-500 flex flex-col gap-0.5">
                <span>
                  Merged from: <span className="text-gray-700">{donor.merge.labels.join('; ')}</span>
                </span>
                <span>
                  Merged{donor.merge.createdBy ? ` by ${donor.merge.createdBy}` : ''} on {formatDate(donor.merge.createdAt)}
                  {donor.merge.reason && ` · Reason: ${mergeReasonText(donor.merge.reason, donor.merge.reasonDetail)}`} ·{' '}
                  <button onClick={() => onUnmerge(donor)} className="text-teal-700 hover:text-teal-800 font-medium">
                    Unmerge
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-6 md:w-64 shrink-0">
          <div>
            <div className="font-semibold text-gray-900">{transactions.length}</div>
            <div className="text-xs text-gray-500">{transactions.length === 1 ? 'donation' : 'donations'}</div>
          </div>
          <div>
            <div className="font-semibold text-gray-900">{formatCurrency(total)}</div>
            <div className="text-xs text-gray-500">
              {donor.consented
                ? `+${formatCurrency(giftAidValue(coveredTotal))} Gift Aid`
                : `+${formatCurrency(giftAidValue(total))} if they consent`}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 md:justify-end shrink-0">
          {donor.consented ? (
            <div className="flex flex-col md:items-end gap-0.5">
              <div className="flex flex-wrap items-center gap-2">
                {donor.lastDeclarationId && (
                  <button
                    onClick={() => onDownload(donor)}
                    disabled={isDownloading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
                    </svg>
                    {isDownloading ? 'Opening...' : 'Download form'}
                  </button>
                )}
                <button
                  onClick={handleGenerateClick}
                  disabled={isGenerating || showForm}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded hover:bg-blue-100 disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {isGenerating ? 'Generating...' : (donor.lastGeneratedAt ? 'Generate again' : 'Generate form')}
                </button>
              </div>
              <span className="text-xs text-gray-500">
                {donor.lastGeneratedAt ? `Last generated ${formatDate(donor.lastGeneratedAt)}` : 'Never generated'}
              </span>
            </div>
          ) : (
            !showForm && (
              <button
                onClick={handleGenerateClick}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 text-sm rounded hover:bg-purple-100"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Claim Gift Aid
              </button>
            )
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Hide donations' : 'Show donations'}
            aria-expanded={isExpanded}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
          >
            <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 rounded-b-lg">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-1.5 pr-3 font-medium">Date</th>
                  <th className="py-1.5 pr-3 font-medium">Reference</th>
                  <th className="py-1.5 pr-3 font-medium">Donation type</th>
                  <th className="py-1.5 pr-3 font-medium">Platform</th>
                  <th className="py-1.5 pr-3 font-medium">Gift Aid ticked</th>
                  <th className="py-1.5 pr-3 font-medium">Covered</th>
                  <th className="py-1.5 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.map((t) => (
                  <tr key={t.id}>
                    <td className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">{formatDate(t.date)}</td>
                    <td className="py-1.5 pr-3 text-teal-600 font-medium whitespace-nowrap">{t.reference}</td>
                    <td className="py-1.5 pr-3 text-gray-700">{t.donationType}</td>
                    <td className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">{getDisplayPlatformName(t.platform, t.device)}</td>
                    <td className="py-1.5 pr-3 text-gray-700">{t.giftAid ? 'Yes' : 'No'}</td>
                    <td className="py-1.5 pr-3 text-gray-700">{t.giftAidCovered ? 'Yes' : 'No'}</td>
                    <td className="py-1.5 text-right text-gray-900 whitespace-nowrap">{formatCurrency(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showForm && (
            <DeclarationForm
              donor={donor}
              requireConfirmation={!donor.consented}
              isSubmitting={isGenerating}
              onSubmit={(req) => onGenerate(donor, req)}
              onCancel={() => setShowForm(false)}
            />
          )}
        </div>
      )}
    </div>
  )
}

// tailwind.config.js replaces Tailwind's inset scale, so classes like inset-0 and
// bottom-4 don't exist; fixed positions are set inline instead
const FULL_SCREEN: React.CSSProperties = { top: 0, right: 0, bottom: 0, left: 0 }
const BOTTOM_CENTER: React.CSSProperties = { bottom: '1rem', left: '50%', transform: 'translateX(-50%)' }

interface ModalProps {
  title: string
  onClose: () => void
  children: React.ReactNode
}

const Modal: React.FC<ModalProps> = ({ title, onClose, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed z-50 flex items-start sm:items-center justify-center bg-gray-900/50 p-4 overflow-auto"
      style={FULL_SCREEN}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div role="dialog" aria-modal="true" aria-label={title} className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

const checkboxClass = 'mt-0.5 h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500'

// Donor names listed per problem before "and N more"
const MAX_LISTED_DONORS = 5

interface ExportDialogProps {
  period: ClaimPeriod
  periodDonors: PeriodDonor[]
  onClose: () => void
}

const ExportDialog: React.FC<ExportDialogProps> = ({ period, periodDonors, onClose }) => {
  const [sheets, setSheets] = useState<SheetId[]>(['hmrc', 'totals', 'donations'])
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx')
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hmrcRows = useMemo(() => buildHmrcRows(periodDonors), [periodDonors])
  const problemCount = countHmrcProblems(hmrcRows)
  const issueSummary = useMemo(() => summarizeHmrcIssues(hmrcRows), [hmrcRows])
  const hmrcTotal = hmrcRows.reduce((sum, r) => sum + r.amount, 0)
  const donationCount = periodDonors.reduce((sum, p) => sum + p.transactions.length, 0)
  const tag = periodFileTag(period)
  const fileName = format === 'xlsx' ? `GiftAid_${tag}.xlsx` : `GiftAid_HMRC_${tag}.csv`

  const toggleSheet = (id: SheetId) =>
    setSheets(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]))

  const sheetOptions: { id: SheetId; label: string; detail: string }[] = [
    {
      id: 'hmrc',
      label: 'HMRC claim: consented donor totals',
      detail: `One row per donor, laid out like the donations table in HMRC's Gift Aid schedule. ${hmrcRows.length} donors · ${formatCurrency(Math.round(hmrcTotal * 100))}`,
    },
    {
      id: 'totals',
      label: 'Donor totals: consented and not consented',
      detail: `Every donor, grouped by consent, with subtotals. ${periodDonors.length} donors`,
    },
    {
      id: 'donations',
      label: 'All donations',
      detail: `One row per donation, with the app's Gift Aid tick. ${donationCount} rows`,
    },
  ]

  const canExport = format === 'csv' ? hmrcRows.length > 0 : sheets.length > 0 && periodDonors.length > 0

  const handleExport = async () => {
    setIsExporting(true)
    setError(null)
    try {
      const blob = format === 'csv' ? buildHmrcCsv(hmrcRows) : await buildWorkbook(periodDonors, sheets)
      saveBlob(blob, fileName)
      onClose()
    } catch (err) {
      console.error('Failed to export Gift Aid spreadsheet:', err)
      setError('Failed to create the spreadsheet. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Modal title="Export spreadsheet" onClose={onClose}>
      <div className="text-sm bg-gray-50 rounded-lg px-3 py-2 text-gray-700">
        <span className="font-medium">{period.label}</span> · {formatDate(period.from)} – {formatDate(period.to)}
      </div>

      {period.id === 'all' && (
        <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900">
          This covers 4 years. HMRC only lets you add up a donor's donations within one accounting period, so choose a tax year for a claim. It also stops donations being claimed twice.
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-gray-500">Format</span>
        <div className="grid grid-cols-2 border border-gray-300 rounded-lg overflow-hidden text-sm" role="radiogroup">
          {([['xlsx', 'Excel workbook (.xlsx)'], ['csv', 'CSV, HMRC sheet only']] as const).map(([id, label]) => (
            <button
              key={id}
              role="radio"
              aria-checked={format === id}
              onClick={() => setFormat(id)}
              className={`px-3 py-2 ${format === id ? 'bg-teal-50 text-teal-800 font-medium' : 'text-gray-700 hover:bg-gray-50'} ${id === 'csv' ? 'border-l border-gray-300' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {format === 'xlsx' && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-gray-500">Sheets</span>
          {sheetOptions.map(opt => (
            <label key={opt.id} className="flex items-start gap-2.5 border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50">
              <input type="checkbox" checked={sheets.includes(opt.id)} onChange={() => toggleSheet(opt.id)} className={checkboxClass} />
              <span>
                <span className="block text-sm font-medium text-gray-800">{opt.label}</span>
                <span className="block text-xs text-gray-500">{opt.detail}</span>
              </span>
            </label>
          ))}
        </div>
      )}

      {hmrcRows.length > HMRC_MAX_ROWS && (format === 'csv' || sheets.includes('hmrc')) && (
        <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900">
          HMRC takes up to {HMRC_MAX_ROWS.toLocaleString('en-GB')} lines per schedule and this claim has {hmrcRows.length.toLocaleString('en-GB')}.
          Split it into more than one claim, for example by choosing a shorter period.
        </div>
      )}

      {problemCount > 0 && (format === 'csv' || sheets.includes('hmrc')) && (
        <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900 flex flex-col gap-2">
          <span className="font-semibold text-amber-800">
            {problemCount} {problemCount === 1 ? 'donor needs' : 'donors need'} checking before sending to HMRC
          </span>
          {issueSummary.map(issue => (
            <div key={issue.code} className="flex flex-col gap-0.5">
              <span className="font-semibold text-amber-800">{issue.what}</span>
              <span>{issue.why}</span>
              <span className="text-xs">
                {issue.donors.slice(0, MAX_LISTED_DONORS).join(' · ')}
                {issue.donors.length > MAX_LISTED_DONORS && ` and ${issue.donors.length - MAX_LISTED_DONORS} more`}
              </span>
            </div>
          ))}
          <span className="text-xs border-t border-dashed border-amber-200 pt-1.5">
            {format === 'xlsx'
              ? 'Each one is highlighted in the HMRC claim sheet, with a note on the cell.'
              : "A CSV can't highlight cells, so fix these in the file before submitting, or download the Excel workbook to see them highlighted."}
          </span>
        </div>
      )}

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      <div className="flex flex-wrap justify-end items-center gap-2">
        <button onClick={onClose} disabled={isExporting} className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800">
          Cancel
        </button>
        <button
          onClick={handleExport}
          disabled={!canExport || isExporting}
          className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50"
        >
          {isExporting ? 'Creating...' : `Download ${fileName}`}
        </button>
      </div>
    </Modal>
  )
}

interface ZipDialogProps {
  period: ClaimPeriod
  periodDonors: PeriodDonor[]
  onFormsGenerated: () => Promise<void>
  onClose: () => void
}

const ZipDialog: React.FC<ZipDialogProps> = ({ period, periodDonors, onFormsGenerated, onClose }) => {
  const [isBuilding, setIsBuilding] = useState(false)
  const [result, setResult] = useState<FormsZipResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [failed, setFailed] = useState<string[]>([])

  const inScope = claimableDonors(periodDonors)
  const ready = inScope.filter(p => p.donor.lastDeclarationId)
  const missing = inScope.filter(p => !p.donor.lastDeclarationId)
  const canGenerate = missing.filter(p => hasFullAddress(p.donor))
  const needAddress = missing.filter(p => !hasFullAddress(p.donor))
  const isGenerating = progress !== null
  const busy = isBuilding || isGenerating

  // One at a time, so a failure for one donor doesn't stop the rest
  const handleGenerateMissing = async () => {
    setError(null)
    setFailed([])
    setProgress({ done: 0, total: canGenerate.length })
    const failedNames: string[] = []
    for (let i = 0; i < canGenerate.length; i++) {
      const donor = canGenerate[i].donor
      try {
        await giftAidApi.createMultiDeclaration(requestFromDonor(donor))
      } catch (err) {
        console.error(`Failed to generate declaration for ${donor.key}:`, err)
        failedNames.push(donorName(donor))
      }
      setProgress({ done: i + 1, total: canGenerate.length })
    }
    try {
      await onFormsGenerated()
    } finally {
      setFailed(failedNames)
      setProgress(null)
      setConfirmed(false)
    }
  }

  const handleDownload = async () => {
    setIsBuilding(true)
    setError(null)
    try {
      const ids = ready.map(p => p.donor.lastDeclarationId as string)
      const zip = await giftAidApi.createFormsZip(ids, `GiftAid_Forms_${periodFileTag(period)}`)
      setResult(zip)
      window.location.href = zip.url
    } catch (err) {
      console.error('Failed to create forms ZIP:', err)
      setError(apiErrorMessage(err, 'Failed to create the ZIP. Please try again.'))
    } finally {
      setIsBuilding(false)
    }
  }

  return (
    <Modal title="Download multiple-donation forms" onClose={busy ? () => {} : onClose}>
      <div className="text-sm bg-gray-50 rounded-lg px-3 py-2 text-gray-700">
        <span className="font-medium">{period.label}</span> · consented donors with Gift Aid donations in this period
      </div>

      {result ? (
        <div className="text-sm bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-green-900 flex flex-col gap-1">
          <span>
            <span className="font-semibold">{result.fileName}</span> is ready with {result.fileCount} {result.fileCount === 1 ? 'form' : 'forms'}.
            {result.missing.length > 0 && ` ${result.missing.length} couldn't be found and were left out.`}
          </span>
          <a href={result.url} className="text-teal-700 hover:text-teal-800 underline w-fit">
            Download again
          </a>
          <span className="text-xs text-green-800">The link works for 30 minutes.</span>
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2 text-sm text-gray-800">
            <span className="text-2xl font-bold text-gray-900">{ready.length}</span>
            {ready.length === 1 ? 'form' : 'forms'} ready, named by surname with an index file
          </div>

          {canGenerate.length > 0 && (
            <div className="text-sm bg-purple-50 border border-purple-200 rounded-lg px-3 py-3 text-gray-800 flex flex-col gap-2">
              <span>
                <span className="font-semibold">{canGenerate.length} {canGenerate.length === 1 ? 'donor has' : 'donors have'} no form yet.</span>{' '}
                Generate one for each so they're in the ZIP.
              </span>
              <span className="text-xs text-gray-600">
                {canGenerate.slice(0, 5).map(p => donorName(p.donor)).join(', ')}
                {canGenerate.length > 5 && ` and ${canGenerate.length - 5} more`}
              </span>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={busy}
                  className="mt-0.5 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-xs text-gray-700">
                  These donors have agreed to Gift Aid all their donations from the past 4 years and any future donations.
                  Each form covers all of the donor's donations, including ones they didn't tick.
                </span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleGenerateMissing}
                  disabled={!confirmed || busy}
                  className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  {progress
                    ? `Generating ${Math.min(progress.done + 1, progress.total)} of ${progress.total}...`
                    : `Generate ${canGenerate.length} ${canGenerate.length === 1 ? 'form' : 'forms'}`}
                </button>
              </div>
            </div>
          )}

          {needAddress.length > 0 && (
            <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900 flex flex-col gap-1">
              <span>
                <span className="font-semibold">{needAddress.length} {needAddress.length === 1 ? 'donor needs' : 'donors need'} an address</span>{' '}
                before a form can be generated. Use Claim Gift Aid on their card.
              </span>
              <span className="text-xs text-amber-800">
                {needAddress.slice(0, 5).map(p => donorName(p.donor)).join(', ')}
                {needAddress.length > 5 && ` and ${needAddress.length - 5} more`}
              </span>
            </div>
          )}

          {failed.length > 0 && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              Couldn't generate forms for {failed.join(', ')}. Try again, or generate them from their cards.
            </div>
          )}

          {isBuilding && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
              Bundling {ready.length} forms. This can take a few seconds.
            </div>
          )}
        </>
      )}

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      <div className="flex flex-wrap justify-end items-center gap-2">
        <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800">
          {result ? 'Close' : 'Cancel'}
        </button>
        {!result && (
          <button
            onClick={handleDownload}
            disabled={ready.length === 0 || busy}
            className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50"
          >
            {isBuilding ? 'Preparing ZIP...' : `Download ${ready.length} ${ready.length === 1 ? 'form' : 'forms'}`}
          </button>
        )}
      </div>
    </Modal>
  )
}

const mergeReasonText = (reason: string, detail: string): string => {
  const label = MERGE_REASONS.find(r => r.id === reason)?.label || reason
  return reason === 'other' && detail ? detail : label
}

// "Aaqib Hafeez, 13 Patch Croft Road, Manchester, M22 5JG", as stored on a merge
const donorLabel = (d: GiftAidDonor): string =>
  [donorName(d), d.addressLine1, d.city, d.postcode].filter(Boolean).join(', ')

const fullAddress = (d: GiftAidDonor): string =>
  [d.addressLine1, d.addressLine2, d.city, d.postcode].filter(Boolean).join(', ')

// Same full name, ignoring case and spacing, for spotting possible duplicates
const nameKey = (d: GiftAidDonor): string =>
  `${d.firstName} ${d.lastName}`.toLowerCase().split(/\s+/).filter(Boolean).join(' ')

interface MergeOption {
  label: string
  donor: GiftAidDonor
}

// One option per distinct value, keeping the first donor that has it
const distinctOptions = (donors: GiftAidDonor[], valueOf: (d: GiftAidDonor) => string): MergeOption[] => {
  const seen = new Map<string, MergeOption>()
  for (const d of donors) {
    const value = valueOf(d)
    if (!seen.has(value)) seen.set(value, { label: value, donor: d })
  }
  return Array.from(seen.values())
}

interface MergeFieldProps {
  name: string
  label: string
  options: MergeOption[]
  selected: number
  emptyLabel: string
  onSelect: (index: number) => void
}

const MergeField: React.FC<MergeFieldProps> = ({ name, label, options, selected, emptyLabel, onSelect }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-1.5 sm:gap-3 py-2 border-t border-gray-100">
    <span className="text-xs font-medium text-gray-500 sm:pt-2">{label}</span>
    {options.length === 1 ? (
      <span className="text-sm text-gray-600 italic sm:pt-1.5">{options[0].label || emptyLabel} (same on all)</span>
    ) : (
      <div className="flex flex-col gap-1.5" role="radiogroup" aria-label={label}>
        {options.map((opt, i) => (
          <label
            key={`${name}-${i}`}
            className={`flex items-start gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer ${
              selected === i ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <input
              type="radio"
              name={name}
              checked={selected === i}
              onChange={() => onSelect(i)}
              className="mt-0.5 h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
            />
            <span className={opt.label ? 'text-gray-800' : 'text-gray-500'}>{opt.label || emptyLabel}</span>
          </label>
        ))}
      </div>
    )}
  </div>
)

interface MergeDialogProps {
  donors: GiftAidDonor[]
  onMerged: (mergeId: string, name: string) => Promise<void>
  onClose: () => void
}

const MergeDialog: React.FC<MergeDialogProps> = ({ donors, onMerged, onClose }) => {
  // Default to the donor with the latest form, otherwise the one with most donations
  const base = useMemo(() => {
    const withForm = donors.filter(d => d.lastGeneratedAt).sort((a, b) => (b.lastGeneratedAt || '').localeCompare(a.lastGeneratedAt || ''))
    return withForm[0] || donors.slice().sort((a, b) => b.transactionCount - a.transactionCount)[0]
  }, [donors])

  const nameOptions = useMemo(() => distinctOptions(donors, donorName), [donors])
  const addressOptions = useMemo(() => distinctOptions(donors, fullAddress), [donors])
  const emailOptions = useMemo(() => distinctOptions(donors, d => d.email), [donors])
  const indexFor = (options: MergeOption[], valueOf: (d: GiftAidDonor) => string) =>
    Math.max(0, options.findIndex(o => o.label === valueOf(base)))

  const [nameIdx, setNameIdx] = useState(() => indexFor(nameOptions, donorName))
  const [addressIdx, setAddressIdx] = useState(() => indexFor(addressOptions, fullAddress))
  const [emailIdx, setEmailIdx] = useState(() => indexFor(emailOptions, d => d.email))
  const [confirmed, setConfirmed] = useState(false)
  const [isMerging, setIsMerging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Blocks and warnings come from the API, which checks the real donor data
  const cards = useMemo(() => donors.map(d => ({ identityKeys: d.identityKeys, label: donorLabel(d) })), [donors])
  const [check, setCheck] = useState<MergeCheck | null>(null)
  const [checkError, setCheckError] = useState<string | null>(null)
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set())
  const [reason, setReason] = useState<MergeReason | ''>('')
  const [reasonDetail, setReasonDetail] = useState('')

  useEffect(() => {
    let cancelled = false
    giftAidApi.checkMerge(cards)
      .then(result => { if (!cancelled) setCheck(result) })
      .catch(err => {
        console.error('Failed to check merge:', err)
        if (!cancelled) setCheckError(apiErrorMessage(err, "Couldn't check these donors. Close this and try again."))
      })
    return () => { cancelled = true }
  }, [cards])

  const toggleAcknowledged = (code: string) =>
    setAcknowledged(prev => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })

  const nameFrom = nameOptions[nameIdx].donor
  const addressFrom = addressOptions[addressIdx].donor
  const email = emailOptions[emailIdx].label

  const transactions = useMemo(() => {
    const byId = new Map<string, GiftAidDonorTransaction>()
    donors.forEach(d => d.transactions.forEach(t => byId.set(t.id, t)))
    return Array.from(byId.values())
  }, [donors])
  const total = transactions.reduce((sum, t) => sum + t.amount, 0)

  const hasForm = donors.some(d => d.lastDeclarationId)
  const detailsValid = Boolean(
    nameFrom.firstName && nameFrom.lastName && addressFrom.addressLine1 && addressFrom.city && addressFrom.postcode
  )
  const blocked = Boolean(check && check.blocks.length > 0)
  const warningsAcknowledged = Boolean(check && check.warnings.every(w => acknowledged.has(w.code)))
  const reasonGiven = reason !== '' && (reason !== 'other' || reasonDetail.trim() !== '')
  const canMerge = Boolean(check) && !blocked && warningsAcknowledged && reasonGiven && detailsValid && (hasForm || confirmed)

  const handleMerge = async () => {
    setIsMerging(true)
    setError(null)
    let mergeId = ''
    try {
      await openPdf(async () => {
        const result = await giftAidApi.mergeDonors({
          title: nameFrom.title,
          firstName: nameFrom.firstName,
          lastName: nameFrom.lastName,
          addressLine1: addressFrom.addressLine1,
          addressLine2: addressFrom.addressLine2,
          city: addressFrom.city,
          postcode: addressFrom.postcode,
          email,
          transactionIds: transactions.map(t => t.id),
          donors: cards,
          reason: reason as MergeReason,
          reasonDetail: reasonDetail.trim(),
          acknowledgedWarnings: check ? check.warnings.map(w => w.code) : [],
        })
        mergeId = result.mergeId
        return result.url
      })
      await onMerged(mergeId, `${nameFrom.firstName} ${nameFrom.lastName}`)
    } catch (err) {
      console.error('Failed to merge donors:', err)
      setError(apiErrorMessage(err, 'Failed to merge the donors. Please try again.'))
      setIsMerging(false)
    }
  }

  return (
    <Modal title={`Merge ${donors.length} donors into one`} onClose={isMerging ? () => {} : onClose}>
      <p className="text-sm text-gray-600">
        Pick the details to go on the new Gift Aid form. All donations from these cards will belong to one donor, and future
        donations from any of them will join it.
      </p>

      {!check && !checkError && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
          Checking these donors...
        </div>
      )}
      {checkError && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{checkError}</div>}

      {check && check.blocks.length > 0 && (
        <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex flex-col gap-1">
          <span className="font-semibold">These donors can't be merged</span>
          {check.blocks.map(b => <span key={b.code}>{b.message}</span>)}
        </div>
      )}

      <div className="flex flex-col">
        <MergeField name="merge-name" label="Name" options={nameOptions} selected={nameIdx} emptyLabel="No name" onSelect={setNameIdx} />
        <MergeField name="merge-address" label="Address" options={addressOptions} selected={addressIdx} emptyLabel="No address" onSelect={setAddressIdx} />
        <MergeField name="merge-email" label="Email" options={emailOptions} selected={emailIdx} emptyLabel="None" onSelect={setEmailIdx} />
      </div>

      <div className="grid grid-cols-3 gap-3 bg-gray-50 rounded-lg px-3 py-2 text-sm">
        <div><div className="text-xs text-gray-500">Donations</div><div className="font-semibold text-gray-900">{transactions.length}</div></div>
        <div><div className="text-xs text-gray-500">Total</div><div className="font-semibold text-gray-900">{formatCurrency(total)}</div></div>
        <div><div className="text-xs text-gray-500">Gift Aid</div><div className="font-semibold text-gray-900">+{formatCurrency(giftAidValue(total))}</div></div>
      </div>

      <div className="text-sm bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 text-gray-800 flex flex-col gap-0.5">
        <span className="font-semibold text-purple-700">A new Gift Aid form will be generated</span>
        <span>
          {donorName(nameFrom)}, {fullAddress(addressFrom) || 'no address'}, covering all {transactions.length} donations. It replaces
          their current forms, which stay in history.
        </span>
      </div>

      {!hasForm && (
        <label className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span>
            The donor has agreed to Gift Aid all their donations from the past 4 years and any future donations to Ross Street Masjid.
          </span>
        </label>
      )}

      {check && !blocked && check.warnings.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-gray-800">Check these before merging</span>
          <span className="text-xs text-gray-500">Merging two different people puts one person's donations on another's Gift Aid form. Tick each one to confirm you've checked it.</span>
          {check.warnings.map(w => (
            <label key={w.code} className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-gray-800 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged.has(w.code)}
                onChange={() => toggleAcknowledged(w.code)}
                className="mt-0.5 h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
              />
              <span>{w.message}</span>
            </label>
          ))}
        </div>
      )}

      {check && !blocked && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-sm font-semibold text-gray-800 mb-1">How do you know these are the same person?</legend>
          {MERGE_REASONS.map(r => (
            <label key={r.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="radio"
                name="merge-reason"
                checked={reason === r.id}
                onChange={() => setReason(r.id)}
                className="h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
              />
              {r.label}
            </label>
          ))}
          {reason === 'other' && (
            <textarea
              id="merge-reason-detail"
              value={reasonDetail}
              onChange={(e) => setReasonDetail(e.target.value)}
              rows={2}
              placeholder="How do you know?"
              aria-label="How do you know these are the same person?"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          )}
          <span className="text-xs text-gray-500">This is saved with the merge, with your name and the date.</span>
        </fieldset>
      )}

      {!detailsValid && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          The chosen name and address need a first name, last name, first line of address, city and postcode. Pick another option, or
          add the details from the donor's card first.
        </div>
      )}

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      <div className="flex flex-wrap justify-end items-center gap-2">
        <button onClick={onClose} disabled={isMerging} className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800">
          Cancel
        </button>
        <button
          onClick={handleMerge}
          disabled={!canMerge || isMerging}
          className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50"
        >
          {isMerging ? 'Merging...' : 'Merge and generate form'}
        </button>
      </div>
    </Modal>
  )
}

interface UnmergeDialogProps {
  donor: GiftAidDonor
  onConfirm: () => Promise<void>
  onClose: () => void
}

const UnmergeDialog: React.FC<UnmergeDialogProps> = ({ donor, onConfirm, onClose }) => {
  const [isUnmerging, setIsUnmerging] = useState(false)
  const labels = donor.merge?.labels || []

  const handleConfirm = async () => {
    setIsUnmerging(true)
    await onConfirm()
  }

  return (
    <Modal title={`Unmerge ${donorName(donor)}?`} onClose={isUnmerging ? () => {} : onClose}>
      <div className="text-sm text-gray-700 flex flex-col gap-2">
        <span>This splits the donor back into the cards that were merged:</span>
        <ul className="list-disc pl-5 text-gray-600">
          {labels.map(l => <li key={l}>{l}</li>)}
        </ul>
        <span>
          The Gift Aid form generated by the merge will be cancelled, and each card goes back to the form it had before (if any).
        </span>
      </div>
      <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900">
        If you've already sent the merged form to HMRC, keep it and don't unmerge.
      </div>
      <div className="flex flex-wrap justify-end items-center gap-2">
        <button onClick={onClose} disabled={isUnmerging} className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800">
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={isUnmerging}
          className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50"
        >
          {isUnmerging ? 'Unmerging...' : 'Unmerge and cancel form'}
        </button>
      </div>
    </Modal>
  )
}

const GiftAidPage: React.FC = () => {
  const [donors, setDonors] = useState<GiftAidDonor[]>([])
  const [unmatchedCount, setUnmatchedCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [generatingKey, setGeneratingKey] = useState<string | null>(null)
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null)
  const [dialog, setDialog] = useState<'export' | 'zip' | 'merge' | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [unmergeDonor, setUnmergeDonor] = useState<GiftAidDonor | null>(null)
  // Offered next to the success message straight after a merge
  const [undoMerge, setUndoMerge] = useState<{ id: string; name: string } | null>(null)

  const periodOptions = useMemo(() => claimPeriodOptions(), [])
  const [periodId, setPeriodId] = useState('all')
  const [customFrom, setCustomFrom] = useState(periodOptions[1]?.from ?? periodOptions[0].from)
  const [customTo, setCustomTo] = useState(periodOptions[0].to)
  const customValid = Boolean(customFrom && customTo && customFrom <= customTo)
  const period: ClaimPeriod = useMemo(() => {
    if (periodId === 'custom') return customValid ? customPeriod(customFrom, customTo) : periodOptions[0]
    return periodOptions.find(p => p.id === periodId) || periodOptions[0]
  }, [periodId, customValid, customFrom, customTo, periodOptions])

  const loadDonors = useCallback(async () => {
    try {
      const result = await giftAidApi.listDonors()
      setDonors(result.donors)
      setUnmatchedCount(result.unmatchedCount)
    } catch (err) {
      console.error('Failed to load Gift Aid donors:', err)
      setError('Failed to load donors')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadDonors()
  }, [loadDonors])

  const handleGenerate = async (donor: GiftAidDonor, req?: MultiDeclarationRequest) => {
    const request: MultiDeclarationRequest = req || requestFromDonor(donor)

    setGeneratingKey(donor.key)
    setError(null)
    setNotice(null)
    setUndoMerge(null)
    try {
      await openPdf(() => giftAidApi.createMultiDeclaration(request))
      setNotice(`Generated a multiple-donation form for ${request.firstName} ${request.lastName}.`)
      await loadDonors()
    } catch (err) {
      console.error('Failed to generate declaration:', err)
      setError(apiErrorMessage(err, 'Failed to generate the Gift Aid form. Please try again.'))
    } finally {
      setGeneratingKey(null)
    }
  }

  const handleDownload = async (donor: GiftAidDonor) => {
    if (!donor.lastDeclarationId) return
    const declarationId = donor.lastDeclarationId
    setDownloadingKey(donor.key)
    setError(null)
    try {
      await openPdf(() => giftAidApi.getMultiDeclarationPdfUrl(declarationId))
    } catch (err) {
      console.error('Failed to download declaration:', err)
      setError(apiErrorMessage(err, 'Failed to open the Gift Aid form. Please try again.'))
    } finally {
      setDownloadingKey(null)
    }
  }

  const closeDialog = useCallback(() => setDialog(null), [])

  const toggleSelect = (donor: GiftAidDonor) =>
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(donor.key)) next.delete(donor.key)
      else next.add(donor.key)
      return next
    })
  // Keys can change when donors are regrouped, so only count ones still on the page.
  // Memoised so the merge dialog only re-checks when the selection actually changes.
  const selectedDonors = useMemo(() => donors.filter(d => selectedKeys.has(d.key)), [donors, selectedKeys])

  const handleMerged = async (mergeId: string, name: string) => {
    setDialog(null)
    setSelectedKeys(new Set())
    setError(null)
    setNotice(`Merged donors and generated a new Gift Aid form for ${name}.`)
    setUndoMerge(mergeId ? { id: mergeId, name } : null)
    await loadDonors()
  }

  const handleUnmerge = async (mergeId: string, name: string) => {
    setError(null)
    setUndoMerge(null)
    try {
      await giftAidApi.unmergeDonors(mergeId)
      setNotice(`Unmerged ${name}. The form generated by the merge was cancelled.`)
      await loadDonors()
    } catch (err) {
      console.error('Failed to unmerge donors:', err)
      setNotice(null)
      setError(apiErrorMessage(err, 'Failed to unmerge the donors. Please try again.'))
    } finally {
      setUnmergeDonor(null)
    }
  }

  const periodDonors = useMemo(() => donorsInPeriod(donors, period), [donors, period])
  const consented = periodDonors.filter(p => p.donor.consented)
  const notConsented = periodDonors.filter(p => !p.donor.consented)
  const consentedCovered = consented.reduce((sum, p) => sum + p.coveredTotal, 0)
  const notConsentedTotal = notConsented.reduce((sum, p) => sum + p.total, 0)

  const q = searchQuery.trim().toLowerCase()
  const matchesSearch = ({ donor: d }: PeriodDonor) =>
    !q ||
    `${d.firstName} ${d.lastName}`.toLowerCase().includes(q) ||
    d.email.toLowerCase().includes(q) ||
    d.postcode.toLowerCase().replace(/\s/g, '').includes(q.replace(/\s/g, ''))

  // Same full name on more than one donor card, usually a different address
  const possibleDuplicates = useMemo(() => {
    const byName = new Map<string, PeriodDonor[]>()
    periodDonors.forEach(p => {
      const key = nameKey(p.donor)
      byName.set(key, [...(byName.get(key) || []), p])
    })
    return Array.from(byName.values())
      .filter(group => group.length > 1)
      .flat()
  }, [periodDonors])

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: donors.length },
    { id: 'consented', label: 'Consented', count: consented.length },
    { id: 'not_consented', label: 'No consent yet', count: notConsented.length },
    { id: 'duplicates', label: 'Possible duplicates', count: possibleDuplicates.length },
  ]

  const sections = tab === 'duplicates'
    ? [{
        id: 'duplicates' as Tab,
        title: 'Possible duplicates',
        subtitle: "Same name at different addresses. Only merge them if you know they're the same person.",
        donors: possibleDuplicates.filter(matchesSearch),
      }]
    : [
        { id: 'consented' as Tab, title: 'Consented', subtitle: 'Ticked Gift Aid online or at the masjid', donors: consented.filter(matchesSearch) },
        { id: 'not_consented' as Tab, title: 'No consent yet', subtitle: 'Add their details to claim Gift Aid', donors: notConsented.filter(matchesSearch) },
      ].filter(s => tab === 'all' || tab === s.id)

  return (
    <div className="flex flex-col h-screen">
      <Navbar />
      <div className={`md:container mx-auto flex-1 pt-8 px-4 overflow-auto ${selectedKeys.size > 0 ? 'pb-24' : 'pb-8'}`}>
        <div className="w-full max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-800">Gift Aid Donors</h1>
            <p className="text-gray-600 text-sm mt-1">
              One multiple-donation form per person. It covers their donations from the last 4 years and any future ones.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
              <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
                Dismiss
              </button>
            </div>
          )}

          {notice && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
              {notice}
              {undoMerge && (
                <button
                  onClick={() => handleUnmerge(undoMerge.id, undoMerge.name)}
                  className="ml-2 font-medium text-green-700 hover:text-green-900 underline"
                >
                  Undo
                </button>
              )}
              <button onClick={() => { setNotice(null); setUndoMerge(null) }} className="ml-2 text-green-600 hover:text-green-800">
                Dismiss
              </button>
            </div>
          )}

          {/* Claim period and exports */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="claim-period" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Claim period
                </label>
                <select
                  id="claim-period"
                  value={periodId}
                  onChange={(e) => setPeriodId(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  {periodOptions.map(p => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                  <option value="custom">Custom dates…</option>
                </select>
                {periodId === 'custom' && (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <input
                      id="claim-from"
                      type="date"
                      aria-label="From"
                      value={customFrom}
                      min={lookbackStart()}
                      max={customTo}
                      onChange={(e) => setCustomFrom(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded"
                    />
                    <span className="text-gray-500">to</span>
                    <input
                      id="claim-to"
                      type="date"
                      aria-label="To"
                      value={customTo}
                      min={customFrom}
                      onChange={(e) => setCustomTo(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setDialog('export')}
                  disabled={loading || periodDonors.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M10 3v18M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" />
                  </svg>
                  Export spreadsheet
                </button>
                <button
                  onClick={() => setDialog('zip')}
                  disabled={loading || consented.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
                  </svg>
                  Download forms (ZIP)
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              {periodId === 'custom' && !customValid
                ? 'Pick a start date before the end date. Showing the last 4 years until then.'
                : `Showing donations from ${formatDate(period.from)} to ${formatDate(period.to)}. Gift Aid figures only count donations it can be claimed on.`}
            </p>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg p-4 text-white">
              <div className="text-2xl font-bold">{consented.length}</div>
              <div className="text-teal-100 text-sm mt-1">
                Consented donors · {formatCurrency(giftAidValue(consentedCovered))} Gift Aid
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-lg p-4 text-white">
              <div className="text-2xl font-bold">{notConsented.length}</div>
              <div className="text-gray-300 text-sm mt-1">Not yet consented</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-amber-700">{formatCurrency(giftAidValue(notConsentedTotal))}</div>
              <div className="text-gray-600 text-sm mt-1">Extra Gift Aid if they consent</div>
            </div>
          </div>

          {/* Tabs and Search */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" role="tablist">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 text-sm rounded-md inline-flex items-center gap-1.5 ${
                    tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t.label}
                  <span className="text-xs bg-gray-200 text-gray-700 px-1.5 rounded-full">{t.count}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-teal-500 sm:w-80">
              <svg className="w-4 h-4 text-gray-400 ml-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, email or postcode..."
                aria-label="Search donors"
                className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none"
              />
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          )}

          {!loading && (
            <div className="flex flex-col gap-6">
              {sections.map((section) => (
                <div key={section.id}>
                  <div className="flex items-baseline gap-2 mb-2">
                    <h2 className="text-sm font-semibold text-gray-800">{section.title}</h2>
                    <span className="text-xs text-gray-500">{section.subtitle}</span>
                  </div>
                  {section.donors.length === 0 ? (
                    <div className="text-center text-sm text-gray-500 py-6 bg-white border border-dashed border-gray-300 rounded-lg">
                      No donors here.
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {section.donors.map((periodDonor) => (
                        <DonorCard
                          key={periodDonor.donor.key}
                          periodDonor={periodDonor}
                          isGenerating={generatingKey === periodDonor.donor.key}
                          isDownloading={downloadingKey === periodDonor.donor.key}
                          onGenerate={handleGenerate}
                          onDownload={handleDownload}
                          selected={selectedKeys.has(periodDonor.donor.key)}
                          onToggleSelect={toggleSelect}
                          onUnmerge={setUnmergeDonor}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {unmatchedCount > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-amber-50 border border-dashed border-amber-300 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">
                      {unmatchedCount} {unmatchedCount === 1 ? 'donation has' : 'donations have'} no donor name
                    </span>{' '}
                    so {unmatchedCount === 1 ? 'it' : 'they'} can't be matched to a person. Add the donor's details from the Transactions page and they will appear here.
                  </p>
                  <Link to="/transactions" className="text-sm text-teal-600 hover:text-teal-700 whitespace-nowrap">
                    View Transactions →
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {dialog === 'export' && <ExportDialog period={period} periodDonors={periodDonors} onClose={closeDialog} />}
      {selectedDonors.length > 0 && (
        <div
          className="fixed z-40 w-[calc(100%-2rem)] max-w-xl bg-gray-900 text-white rounded-lg shadow-lg px-4 py-2.5 flex flex-wrap items-center justify-between gap-3"
          style={BOTTOM_CENTER}
        >
          <span className="text-sm">
            {selectedDonors.length} {selectedDonors.length === 1 ? 'donor' : 'donors'} selected
            {selectedDonors.length === 1 && <span className="text-gray-400"> · select another to merge</span>}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedKeys(new Set())} className="px-2 py-1 text-sm text-gray-300 hover:text-white">
              Clear
            </button>
            <button
              onClick={() => setDialog('merge')}
              disabled={selectedDonors.length < 2}
              className="px-3 py-1.5 bg-white text-gray-900 text-sm font-medium rounded hover:bg-gray-100 disabled:opacity-50"
            >
              Merge {selectedDonors.length >= 2 ? `${selectedDonors.length} donors` : 'donors'}…
            </button>
          </div>
        </div>
      )}

      {dialog === 'merge' && selectedDonors.length >= 2 && (
        <MergeDialog donors={selectedDonors} onMerged={handleMerged} onClose={closeDialog} />
      )}
      {unmergeDonor?.merge && (
        <UnmergeDialog
          donor={unmergeDonor}
          onConfirm={() => handleUnmerge(unmergeDonor.merge!.id, donorName(unmergeDonor))}
          onClose={() => setUnmergeDonor(null)}
        />
      )}
      {dialog === 'zip' && (
        <ZipDialog period={period} periodDonors={periodDonors} onFormsGenerated={loadDonors} onClose={closeDialog} />
      )}
    </div>
  )
}

export default GiftAidPage
